import { createServerFn } from "@tanstack/react-start";

import { requireBavimailBackendJson } from "@/lib/bavimail-api.server";

type SuppressionRecord = {
  id: string;
  user_id: string;
  address: string;
  reason: "bounce" | "complaint" | "manual";
  note: string | null;
  metadata: Record<string, {}>;
  created_at: string;
  updated_at: string;
};

export type SuppressionView = {
  id: string;
  email: string;
  reason: string;
  source: string;
  status: string;
  note: string | null;
  metadata: Record<string, {}>;
  suppressedAt: string;
  releasedAt: string | null;
};

function passthroughValidator<T>(value: T) {
  return value;
}

function mapSuppressionToView(suppression: SuppressionRecord): SuppressionView {
  return {
    id: suppression.id,
    email: suppression.address,
    reason: suppression.reason,
    source: suppression.reason === "manual" ? "user" : "ses",
    status: "active",
    note: suppression.note,
    metadata: suppression.metadata,
    suppressedAt: suppression.created_at,
    releasedAt: null,
  };
}

export const getSuppressionsView = createServerFn({ method: "GET" })
  .validator(passthroughValidator<{ search?: string | null } | undefined>)
  .handler(async ({ data }) => {
    const searchParams = new URLSearchParams({ limit: "100" });
    if (data?.search?.trim()) searchParams.set("search", data.search.trim());

    const result = await requireBavimailBackendJson<{ suppressions: SuppressionRecord[] }>(
      { path: `/suppressions?${searchParams.toString()}` },
      "Failed to load suppressions.",
    );
    return result.suppressions.map(mapSuppressionToView);
  });

export const createSuppressionAction = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ email: string; note?: string | null }>)
  .handler(async ({ data }) => {
    const created = await requireBavimailBackendJson<SuppressionRecord>(
      {
        path: "/suppressions",
        method: "POST",
        json: { address: data.email, ...(data.note ? { note: data.note } : {}) },
      },
      "Failed to create the suppression.",
    );
    return mapSuppressionToView(created);
  });

export const releaseSuppressionAction = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ suppressionId: string }>)
  .handler(async ({ data }) => {
    await requireBavimailBackendJson<null>(
      { path: `/suppressions/${encodeURIComponent(data.suppressionId)}`, method: "DELETE" },
      "Failed to release the suppression.",
    );
    return { id: data.suppressionId };
  });
